<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable, { DataTableRowEditSaveEvent } from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import { useUtilities } from '@apparatix/composables';
import type { Content } from '@apparatix/types/AccountProfiles';
import { Stack } from '@apparatix/library/core';

type Props = {
  contents: Content<'table'>;
  columns: string[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'on:change', data: Content<'table'>): void
}>();

const { arrayHasValues, forKeys } = useUtilities();

const model = ref<object[]>([]);
const editingRows = ref<object[]>([]);

const onRowEditSave = (e: DataTableRowEditSaveEvent) => {
  const { index, newData } = e;
  const data = [...model.value];
  if (index <= data.length - 1) {
    data[index] = newData;
  }

  model.value = data;

  emit('on:change', data);
};

const onAddRow = () => {
  const [shape] = model.value;
  const newItem: object = {};

  forKeys(shape, key => {
    newItem[key] = '';
  });

  model.value.push(newItem);
};

onMounted(() => {
  if (!props.contents && arrayHasValues(props.columns)) {
    model.value = [
      Object.fromEntries(props.columns.map(col => [col, ''])),
    ];
  }

  if (arrayHasValues(props.contents) && arrayHasValues(props.columns)) {
    model.value = [...props.contents];
  }
});
</script>
<template>
  <Stack :gap="2">
    <DataTable
      v-model:editing-rows="editingRows"
      size="small"
      :value="model"
      edit-mode="row"
      @row-edit-save="onRowEditSave"
    >
      <Column
        v-for="(col, idx) in props.columns"
        :key="idx"
        :field="col"
        :header="col"
      >
        <template #editor="{data, field}">
          <InputText v-model="data[field]" size="small" fluid />
        </template>
      </Column>
      <Column row-editor />
    </DataTable>
    <div>
      <Button
        severity="secondary"
        size="small"
        outlined
        @click="onAddRow"
      >
        Add Row
      </Button>
    </div>
  </Stack>
</template>
<style scoped>
:deep(.p-datatable-column-title) {
  text-transform: capitalize;
}
</style>
